import { makeFormStateWithModel } from 'formtastisch'
import { type IntlShape } from 'react-intl'

import { CustomerTypeV1 } from '@redteclab/api/clients/bully'

import { validateMinValue, validateRequired } from '../base-form-handling'
import { TENANT } from '../tenant'

import {
  loginRegistrationRegistrationFormValidateCompanyName,
  loginRegistrationRegistrationFormValidateEmail,
  loginRegistrationRegistrationFormValidateName,
  loginRegistrationRegistrationFormValidatePassword,
  loginRegistrationRegistrationFormValidatePhoneNumber,
  loginRegistrationRegistrationFormValidateVatNumber,
  validateCheckBoxChecked,
} from './registrationFormValidation'

export interface RegistrationFormData {
  birthDaySelectedDay: number
  birthDaySelectedMonth: number
  birthDaySelectedYear: number
  companyName: string
  customerType: string
  email: string
  fcSolution: string
  firstName: string
  lastName: string
  newsletterAccepted: boolean
  password: string
  phone: string
  salutation: string
  tosAccepted: boolean
  vatNumber: string
}

const MIN_VALUE_BIRTHDATE_DROP_BOX = 1

export const initialRegistrationFormData = (
  isProfessionalRegistration: boolean,
  tenant?: TENANT,
): RegistrationFormData => {
  return {
    birthDaySelectedDay: 0,
    birthDaySelectedMonth: 0,
    birthDaySelectedYear: 0,
    companyName: '',
    customerType: isProfessionalRegistration
      ? (CustomerTypeV1.Professional as string)
      : (CustomerTypeV1.Customer as string),
    email: '',
    fcSolution: '',
    firstName: '',
    lastName: '',
    newsletterAccepted: TENANT.CH === tenant,
    password: '',
    phone: '',
    salutation: '',
    tosAccepted: false,
    vatNumber: '',
  }
}

interface RegistrationFormStateProps {
  hasVatNumber: boolean | undefined
  initialData: RegistrationFormData
  intl: IntlShape
  isProfessionalRegistration: boolean | undefined
}

export const registrationFormMakeRegistrationFormState = (
  {
    hasVatNumber,
    initialData,
    intl,
    isProfessionalRegistration,
  }: RegistrationFormStateProps,
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types -- the rule was disabled before a comment became required
) => {
  return makeFormStateWithModel({
    initialData,
    validations: {
      birthDaySelectedDay: (value) => {
        return validateMinValue(
          value,
          MIN_VALUE_BIRTHDATE_DROP_BOX,
          intl.formatMessage({ id: 'form.error.input.missing' }),
        )
      },
      birthDaySelectedMonth: (value) => {
        return validateMinValue(
          value,
          MIN_VALUE_BIRTHDATE_DROP_BOX,
          intl.formatMessage({ id: 'form.error.input.missing' }),
        )
      },
      birthDaySelectedYear: (value) => {
        return validateMinValue(
          value,
          MIN_VALUE_BIRTHDATE_DROP_BOX,
          intl.formatMessage({ id: 'form.error.input.missing' }),
        )
      },
      companyName: (value: string) => {
        if (isProfessionalRegistration) {
          return loginRegistrationRegistrationFormValidateCompanyName(
            value,
            intl,
          )
        }

        return { valid: true }
      },
      email: (value) => {
        return loginRegistrationRegistrationFormValidateEmail(value, intl)
      },
      firstName: (value) => {
        return loginRegistrationRegistrationFormValidateName(value, intl)
      },
      lastName: (value) => {
        return loginRegistrationRegistrationFormValidateName(value, intl)
      },
      password: (value, model) => {
        return loginRegistrationRegistrationFormValidatePassword(
          value,
          intl,
          model?.email,
        )
      },
      phone: (value) => {
        return loginRegistrationRegistrationFormValidatePhoneNumber(value, intl)
      },
      salutation: (value) => {
        return validateRequired(
          value,
          intl.formatMessage({
            id: 'registrationForm.messages.validation.salutation.required',
          }),
        )
      },
      tosAccepted: (value) => {
        return validateCheckBoxChecked(
          value,
          intl.formatMessage({
            id: 'registrationForm.messages.validation.tosaccepted.required',
          }),
        )
      },
      vatNumber: (value) => {
        if (isProfessionalRegistration && hasVatNumber) {
          return loginRegistrationRegistrationFormValidateVatNumber(value, intl)
        }

        return { valid: true }
      },
    },
  })
}

export type RegistrationFormState = ReturnType<
  typeof registrationFormMakeRegistrationFormState
>
